import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  HandCoins, Loader2, CheckCircle2, Clock, XCircle, RefreshCw, User, Calendar, MessageSquare,
} from 'lucide-react';
import { supabase } from '../../../../src/lib/supabase';
import { formatCurrency } from '../utils';

// Acompanhamento das cobranças unificadas — a aba que responde "o que foi
// negociado, com quem, por quê, e o cliente pagou?".
//
// É o relatório que o financeiro pediu junto com a automação: sem ele, o motivo
// e o canal da negociação ficariam gravados no banco sem ninguém conseguir ler.
// Cada linha é uma negociação; o status vem do desfecho do boleto, não de
// alguém marcar na mão.

interface CobrancaUnificada {
  id: string;
  cliente_nome: string;
  valor_faturas: number;
  valor_cobrado: number;
  motivo: string;
  canal: string;
  observacao: string;
  status: 'aguardando_pagamento' | 'quitada' | 'cancelada';
  criado_por_nome: string | null;
  created_at: string;
  quitado_em: string | null;
  cancelado_em: string | null;
  fatura_avulsa_id: number;
  faturas_snapshot: Array<{ mes_referencia?: string; uc_codigo?: string; valor?: number }> | null;
}

const ROTULO_STATUS: Record<CobrancaUnificada['status'], { texto: string; classe: string; Icone: typeof Clock }> = {
  aguardando_pagamento: { texto: 'Aguardando pagamento', classe: 'bg-amber-100 text-amber-700 border-amber-200', Icone: Clock },
  quitada: { texto: 'Pago — faturas quitadas', classe: 'bg-green-100 text-green-700 border-green-200', Icone: CheckCircle2 },
  cancelada: { texto: 'Cancelada', classe: 'bg-gray-100 text-gray-600 border-gray-200', Icone: XCircle },
};

const dataBr = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('pt-BR') : '—');

const CobrancasUnificadas: React.FC = () => {
  const [carregando, setCarregando] = useState(true);
  const [linhas, setLinhas] = useState<CobrancaUnificada[]>([]);
  const [filtro, setFiltro] = useState<'todas' | CobrancaUnificada['status']>('todas');

  const carregar = useCallback(async () => {
    setCarregando(true);
    const { data } = await supabase
      .from('cobrancas_unificadas')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(500);
    setLinhas((data || []) as CobrancaUnificada[]);
    setCarregando(false);
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const visiveis = useMemo(
    () => (filtro === 'todas' ? linhas : linhas.filter((l) => l.status === filtro)),
    [linhas, filtro],
  );

  const resumo = useMemo(() => ({
    aguardando: linhas.filter((l) => l.status === 'aguardando_pagamento'),
    quitadas: linhas.filter((l) => l.status === 'quitada'),
  }), [linhas]);

  const soma = (lista: CobrancaUnificada[]) => lista.reduce((s, l) => s + Number(l.valor_cobrado || 0), 0);

  return (
    <div className="p-6 h-full flex flex-col">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Cobranças unificadas</h2>
          <p className="text-sm text-gray-500">
            Negociações que juntaram faturas num boleto só. Quando o boleto é pago, as faturas viram
            Quitado sozinhas.
          </p>
        </div>
        <button
          onClick={carregar}
          className="px-3 py-2 text-xs font-semibold text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
        >
          <RefreshCw size={14} /> Atualizar
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <button
          onClick={() => setFiltro('todas')}
          className={`text-left bg-white rounded-xl border p-4 transition-colors ${filtro === 'todas' ? 'border-[#5F6C37] ring-1 ring-[#5F6C37]/20' : 'border-gray-200 hover:border-gray-300'}`}
        >
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Todas</p>
          <p className="text-2xl font-black text-gray-800 mt-1">{linhas.length}</p>
        </button>
        <button
          onClick={() => setFiltro('aguardando_pagamento')}
          className={`text-left bg-white rounded-xl border p-4 transition-colors ${filtro === 'aguardando_pagamento' ? 'border-amber-500 ring-1 ring-amber-200' : 'border-gray-200 hover:border-gray-300'}`}
        >
          <p className="text-xs font-bold text-amber-600 uppercase tracking-wide">Aguardando pagamento</p>
          <p className="text-2xl font-black text-gray-800 mt-1">{resumo.aguardando.length}</p>
          <p className="text-xs text-gray-500">{formatCurrency(soma(resumo.aguardando))}</p>
        </button>
        <button
          onClick={() => setFiltro('quitada')}
          className={`text-left bg-white rounded-xl border p-4 transition-colors ${filtro === 'quitada' ? 'border-green-600 ring-1 ring-green-200' : 'border-gray-200 hover:border-gray-300'}`}
        >
          <p className="text-xs font-bold text-green-700 uppercase tracking-wide">Recuperado</p>
          <p className="text-2xl font-black text-gray-800 mt-1">{resumo.quitadas.length}</p>
          <p className="text-xs text-gray-500">{formatCurrency(soma(resumo.quitadas))}</p>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {carregando ? (
          <div className="p-12 flex items-center justify-center text-gray-400 gap-2">
            <Loader2 className="animate-spin" size={18} /> Carregando negociações...
          </div>
        ) : visiveis.length === 0 ? (
          <div className="p-12 text-center bg-white border border-dashed border-gray-200 rounded-xl">
            <HandCoins size={40} className="text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">Nenhuma cobrança unificada por aqui.</p>
            <p className="text-xs text-gray-400 mt-1">
              Elas nascem no Perfil 360 do cliente, no botão “Cobrança unificada”.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {visiveis.map((l) => {
              const { texto, classe, Icone } = ROTULO_STATUS[l.status];
              const meses = (l.faturas_snapshot || []).map((f) => f.mes_referencia).filter(Boolean);
              return (
                <div key={l.id} className="bg-white rounded-xl border border-gray-200 p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="font-bold text-gray-800">{l.cliente_nome}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {meses.length} fatura{meses.length === 1 ? '' : 's'}
                        {meses.length > 0 ? ` · ${meses.join(', ')}` : ''}
                      </p>
                    </div>
                    <span className={`shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-bold ${classe}`}>
                      <Icone size={12} /> {texto}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4 pt-4 border-t border-gray-100">
                    <div>
                      <p className="text-[10px] font-bold text-gray-400 uppercase">Soma das faturas</p>
                      <p className="text-sm font-bold text-gray-700">{formatCurrency(l.valor_faturas)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-gray-400 uppercase">Boleto</p>
                      <p className="text-sm font-black text-gray-800">{formatCurrency(l.valor_cobrado)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-gray-400 uppercase">Negociado em</p>
                      <p className="text-sm text-gray-700 flex items-center gap-1">
                        <Calendar size={12} className="text-gray-400" /> {dataBr(l.created_at)}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-gray-400 uppercase">
                        {l.status === 'quitada' ? 'Pago em' : 'Por'}
                      </p>
                      <p className="text-sm text-gray-700 flex items-center gap-1 truncate">
                        {l.status === 'quitada'
                          ? <><CheckCircle2 size={12} className="text-green-600" /> {dataBr(l.quitado_em)}</>
                          : <><User size={12} className="text-gray-400" /> {l.criado_por_nome || '—'}</>}
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-100 flex items-start gap-2">
                    <MessageSquare size={14} className="text-gray-400 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs font-semibold text-gray-700">
                        {l.motivo} <span className="text-gray-400">·</span> {l.canal}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">{l.observacao}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CobrancasUnificadas;
